import { BASE_API } from '@/config/env'
import store from '@/store'
import { isEmpty } from 'lodash-es'
import didiaxios from '@didi/dajax/dist/dajax'
import ERROR_CODE from './error-code'
import Passport, { isWhiteNameForShare } from '../passport/h5passport'
import { getVueInstance } from '../bridge/vue-instance'
import { getGlobalParams } from '@/utils/bridge/bridge.js'
import i18n from '@/i18n/locale'
import Apollo from '@didi/apollo-sdk-javascript'

/**
 * 获取cookie值
 * @param {string} cookieName - cookie名称
 * @returns {string|null} - cookie值
 */
function getCookieValue(cookieName) {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=')
    if (name === cookieName) {
      return decodeURIComponent(value)
    }
  }
  return null
}

/**
 * 获取tripcountry header的灰度配置
 * 如果灰度命中，才需要添加tripcountry header
 * @returns {Promise<boolean>} - 是否命中灰度（true表示需要添加header）
 */
function getTripcountryGrayConfig() {
  return new Promise(resolve => {
    try {
      const APOLLO_NAME = 'B_WEB_TRIPCOUNTRY_HEADER_ENABLE'
      // 从cookie获取_OMGID参数
      const omegaId = getCookieValue('_OMGID')

      // 使用 Apollo SDK 获取灰度配置
      const apolloParams = {
        name: APOLLO_NAME,
        global: true,
        key: Math.random(),
      }

      // 如果有_OMGID，将其作为key传递给Apollo
      if (omegaId) {
        apolloParams.pid = omegaId
        apolloParams.key = omegaId
      }

      // 调用 Apollo SDK
      Apollo.getToggle(apolloParams, result => {
        console.log('getTripcountryGrayConfig result', result)
        try {
          // 参数校验
          if (!result || result.code !== 0 || !result.data) {
            resolve(false)
            return
          }

          const toggleData = result.data[APOLLO_NAME]
          if (!toggleData) {
            resolve(false)
            return
          }
          if (toggleData && toggleData.params.enable === 1) {
            resolve(true)
          } else {
            resolve(false)
          }
        } catch (error) {
          console.warn('获取tripcountry灰度配置失败: parse result error', error)
          resolve(false)
        }
      })
    } catch (error) {
      console.warn('获取tripcountry灰度配置失败:', error)
      resolve(false)
    }
  })
}

/**
 * 获取国家码（优先从currentShop获取，如果没有则从其他来源获取，最后从URL中获取）
 * @returns {string|null} - 国家码
 */
function getCountryCode() {
  const state = store.state

  // 优先从 currentShop 获取 countryCode（与 b-web 保持一致）
  if (state.currentShop?.countryCode) {
    return state.currentShop.countryCode
  }

  // 其次从 currentContractor 获取 country
  if (state.currentContractor?.country) {
    return state.currentContractor.country
  }

  // 再次从 state.country 获取
  if (state.country) {
    return state.country
  }

  // 最后从 URL 路径中获取（例如：/pt-BR/v2/... 中的 BR）
  try {
    const pathname = window.location.pathname
    // 解析路径，例如：/pt-BR/v2/confirmation-entrega/delivery-code
    const pathSegments = pathname.split('/').filter(segment => segment)
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0] // 例如：pt-BR
      // 如果包含 -，取 - 后面的部分作为国家码
      if (firstSegment.includes('-')) {
        const countryCode = firstSegment.split('-')[1]
        if (countryCode) {
          return countryCode.toUpperCase() // 转换为大写，如 BR
        }
      }
    }
  } catch (error) {
    console.warn('从URL获取国家码失败:', error)
  }

  return null
}

// 在模块加载时初始化灰度配置（异步，不阻塞）
let tripcountryGrayEnabled = false
getTripcountryGrayConfig().then(isHit => {
  tripcountryGrayEnabled = isHit
})

async function addGlobalData(data = {}) {
  let params = store.state.globalParams
  if (isEmpty(params)) {
    params = await getGlobalParams()
  }
  return Object.assign({}, params || {}, data)
}

// 获取公共配置
function defaultConfig() {
  return {
    withCredentials: true,
  }
}

// 添加安全sdk请求拦截器
didiaxios.interceptors.request.use(
  async config => {
    // 配置不检测域名绑定-wsgsig配置
    config.noDomainCheck = true
    if (window.$sailingRaven) {
      config.ravenReqId = window.$sailingRaven.trackRequest.start(config.url, config.data)
    }
    
    console.log("tripcountryGrayEnabled>>>", tripcountryGrayEnabled)
    // 添加 tripcountry header（只有在灰度命中的情况下才添加）
    if (tripcountryGrayEnabled) {
      const countryCode = getCountryCode()
      console.log("countryCode>>>", countryCode)
      if (countryCode) {
        config.headers = config.headers || {}
        config.headers.tripcountry = countryCode
      }
    }
    
    // 添加 requesttag header（从 globalParams 中获取，如果有就添加）
    if (store.state.globalParams && store.state.globalParams.requesttag) {
      config.headers = config.headers || {}
      config.headers.requesttag = store.state.globalParams.requesttag
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

didiaxios.interceptors.response.use(function (response) {
  if (window.$sailingRaven) {
    const { config, data: { requestId = '' } = {} } = response
    window.$sailingRaven.trackRequest.end(config.ravenReqId, requestId)
  }
  // 添加passport登陆时请求响应拦截器配置
  if (!isWhiteNameForShare()) {
    if (response && response.data && response.data.errno !== 0) {
      const errno = response.data.errno
      const errmsg = response.data.errmsg
      if (
        errno === ERROR_CODE.NOT_LOGIN ||
        errno === ERROR_CODE.NO_TOKEN ||
        ERROR_CODE.GET_SHOP_INFO_FAILED.includes(errno) ||
        errno === ERROR_CODE.NO_RELEATED_SHOPS
      ) {
        try {
          // 动态获取Vue实例
          const vueInstance = getVueInstance()
          // 确保Vue实例存在且有正确的方法
          if (vueInstance && vueInstance.$createGoDialog) {
            const dialog = vueInstance.$createGoDialog({
              mainBtnText: i18n.t('FoodB_modification_Confirmation_ehVU'), // 确认
              title: errmsg,
              onMainBtn() {
                // 先隐藏对话框
                dialog.hide()
                // Passport.forceLogin 不返回Promise，使用回调方式
                Passport.forceLogin(() => {
                  window.location.reload()
                })
              },
            })
            dialog.show()
          }
        } catch (error) {
          console.error('Dialog creation failed:', error)
        }
        // 显示Toast错误信息
        store.commit('TOAST', { msg: errmsg, type: 'error' })
      }
    }
  }
  return response.data
})

export async function postForm(url = '', data = {}, config = {}) {
  const globalData = await addGlobalData(data)
  return didiaxios.request({
    url: prefixUrl(url),
    method: 'post',
    emulateJSON: true, // 是否使用application/x-www-form-urlencoded来提交
    needEncrypt: true, // 是否加签名
    params: {},
    timeout: 30000,
    // NOTE: 添加携带cookie信息，不然BDAPP过来不携带gToken信息
    // TODO: 观察是否影响BH5原来的业务逻辑
    ...Object.assign({}, defaultConfig(), config),
    body: {
      ...globalData,
    },
  })
}

export function getCommonConfig() {
  const commonOptions = {
    // 考虑到网络因素，超时时间调整为30s
    timeout: 30000,
    headers: {
      Accept: 'application/json',
    },
    withCredentials: false,
    needEncrypt: true,
  }
  return commonOptions
}

// get请求
export async function get(url = '', data = {}, config = {}) {
  const globalData = await addGlobalData(data)
  if (globalData === -1) return
  const params = Object.assign({}, globalData, data)
  const datas = Object.assign({}, getCommonConfig(), { params }, config)
  return didiaxios.get.call(null, prefixUrl(url), datas)
}

/**
 * 适用于文件上传，不依赖el-upload组件
 */
export async function postFormData(url, data = {}, config = {}) {
  const formData = new FormData()
  const commParams = await addGlobalData(data)
  const params = Object.assign({}, commParams, data)
  for (const key in params) {
    formData.append(key, params[key])
  }
  return new Promise((resolve, reject) => {
    const onprogress = config.onprogress || (() => undefined)
    const xhr = new XMLHttpRequest()
    xhr.timeout = 30 * 1000
    xhr.withCredentials = true
    xhr.open('post', prefixUrl(url))
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          try {
            resolve(JSON.parse(xhr.response))
          } catch (e) {
            resolve(xhr.response)
          }
        } else {
          reject(xhr)
        }
      }
    }
    xhr.onerror = reject
    xhr.ontimeout = reject
    xhr.upload.onprogress = onprogress
    xhr.send(formData)
  })
}

export function prefixUrl(url) {
  if (url.indexOf('http') < 0) {
    if (url.indexOf('/') === 0) {
      url = BASE_API + url
    }
  }
  return url
}

export default {
  postForm,
  postFormData,
  get,
}
